import React from 'react';
import { visitorGuidelines } from '../data/mockData';
import { CheckCircle, XCircle, AlertTriangle } from 'lucide-react';

const Guidelines = () => {
  return (
    <div className="min-h-screen bg-[#1a1c1b] pt-20">
      {/* Hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="/images/parit-sulong.jpg"
            alt="Visitor Guidelines"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#1a1c1b] via-[#1a1c1b]/90 to-[#1a1c1b]"></div>
        </div>
        
        <div className="container-custom relative z-10">
          <h1 className="heading-1 text-[#dc2626] mb-6 max-w-3xl">
            Visitor Guidelines
          </h1>
          <p className="body-large text-white/90 max-w-3xl">
            How to visit dark tourism sites with respect, cultural sensitivity, and awareness.
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#302f2c] p-8 rounded-lg border-l-4 border-[#dc2626]">
              <p className="body-large text-white/90 italic mb-4">
                "In Malaysia, places connected to tragedy or loss are best approached through the lenses 
                of education, remembrance, and cultural awareness."
              </p>
              <p className="body-medium text-white/80">
                These guidelines are designed to ensure that visits to dark tourism sites are conducted 
                thoughtfully and respectfully, honoring the histories and people connected to these locations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Do's */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-4 mb-12">
              <CheckCircle className="text-[#dc2626]" size={48} />
              <h2 className="heading-2 text-[#dc2626]">Do's</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {visitorGuidelines.dos.map((guideline, index) => (
                <div 
                  key={index}
                  className="bg-[#0f0f10] p-8 rounded-lg border border-[#3a2222] hover:border-[#dc2626]/50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <CheckCircle className="text-[#dc2626] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="heading-4 text-white mb-3">{guideline.title}</h3>
                      <p className="body-small text-white/70">{guideline.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Don'ts */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <div className="flex items-center gap-4 mb-12">
              <XCircle className="text-[#ff6b6b]" size={48} />
              <h2 className="heading-2 text-[#ff6b6b]">Don'ts</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-6">
              {visitorGuidelines.donts.map((guideline, index) => (
                <div 
                  key={index}
                  className="bg-[#1a1c1b] p-8 rounded-lg border border-[#3a2222] hover:border-[#ff6b6b]/50 transition-colors"
                >
                  <div className="flex items-start gap-4">
                    <XCircle className="text-[#ff6b6b] flex-shrink-0 mt-1" size={24} />
                    <div>
                      <h3 className="heading-4 text-white mb-3">{guideline.title}</h3>
                      <p className="body-small text-white/70">{guideline.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Key Message */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-[#302f2c] p-10 rounded-lg border-l-4 border-[#dc2626]">
              <div className="flex items-start gap-4 mb-6">
                <AlertTriangle className="text-[#dc2626] flex-shrink-0" size={40} />
                <h2 className="heading-2 text-[#dc2626]">Key Message</h2>
              </div>
              
              <div className="space-y-4 body-medium text-white/90">
                <p className="text-xl">
                  These sites represent real loss and real suffering. They are not entertainment venues 
                  or backdrops for social media content.
                </p>
                <p>
                  Respectful engagement means:
                </p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Recognizing the humanity behind the history</li>
                  <li>Acknowledging cultural and religious sensitivities</li>
                  <li>Approaching with humility and a willingness to learn</li>
                  <li>Leaving sites undisturbed and treated with dignity</li>
                </ul>
                <p className="text-[#dc2626] font-semibold text-lg mt-6">
                  When we visit these places with intention and respect, we help ensure their stories 
                  remain meaningful rather than merely curious.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Cultural Sensitivity in Malaysia */}
      <section className="space-section bg-[#0f0f10]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 text-[#dc2626] mb-8">Cultural Sensitivity in Malaysia</h2>
            
            <div className="space-y-6 body-medium text-white/80">
              <p>
                Malaysia's multicultural society means that different communities hold varying beliefs 
                about death, remembrance, and the appropriate ways to engage with sites of tragedy.
              </p>

              <div className="grid md:grid-cols-3 gap-6 my-8">
                <div className="bg-[#1a1c1b] p-6 rounded-lg border border-[#3a2222]">
                  <h4 className="heading-4 text-[#dc2626] mb-3">Islamic Perspectives</h4>
                  <p className="body-small text-white/70">
                    Grave visitation (ziyarat) is permitted in Islam for remembrance and prayer, but 
                    excessive mourning and superstitious practices are discouraged.
                  </p>
                </div>

                <div className="bg-[#1a1c1b] p-6 rounded-lg border border-[#3a2222]">
                  <h4 className="heading-4 text-[#dc2626] mb-3">Chinese Traditions</h4>
                  <p className="body-small text-white/70">
                    Ancestral worship and respect for the dead are central. Cemeteries and grave sites 
                    are approached with reverence, especially during festivals like Qingming.
                  </p>
                </div>

                <div className="bg-[#1a1c1b] p-6 rounded-lg border border-[#3a2222]">
                  <h4 className="heading-4 text-[#dc2626] mb-3">Indigenous Beliefs</h4>
                  <p className="body-small text-white/70">
                    Many indigenous communities in Sabah and Sarawak have sacred burial traditions and 
                    sites that require special permission and protocols for visitation.
                  </p>
                </div>
              </div>

              <div className="bg-[#302f2c] p-8 rounded-lg border-l-4 border-[#dc2626]">
                <p className="text-white/90 font-semibold">
                  Always research the specific cultural context of a site before visiting. When in doubt, 
                  observe how locals behave and follow their lead. Respect "No Entry" signs and private 
                  property boundaries.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photography Guidelines */}
      <section className="space-section bg-[#1a1c1b]">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 text-[#dc2626] mb-8">Photography Guidelines</h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-[#0f0f10] p-8 rounded-lg border-l-4 border-[#dc2626]">
                <h3 className="heading-4 text-white mb-4">Acceptable Photography</h3>
                <ul className="space-y-3 body-small text-white/70">
                  <li>• Documentary-style images showing the site's condition</li>
                  <li>• Architectural details and historical features</li>
                  <li>• Landscape shots providing context</li>
                  <li>• Memorial inscriptions and historical markers</li>
                  <li>• General views that honor the site's significance</li>
                </ul>
              </div>

              <div className="bg-[#0f0f10] p-8 rounded-lg border-l-4 border-[#ff6b6b]">
                <h3 className="heading-4 text-white mb-4">Inappropriate Photography</h3>
                <ul className="space-y-3 body-small text-white/70">
                  <li>• Selfies or posed photos at sites of mass tragedy</li>
                  <li>• Playful or casual photography</li>
                  <li>• Close-ups of individual graves without permission</li>
                  <li>• Images intended to sensationalize or mock</li>
                  <li>• Photography where explicitly prohibited</li>
                </ul>
              </div>
            </div>

            <p className="body-medium text-white/80 mt-8 italic text-center">
              "Ask yourself: Would the people who suffered here approve of how I'm documenting this place?"
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Guidelines;
